import dayjs from "dayjs";

import * as cesRequest from "./CESRequest";
import { FormNames, StatusCodes } from "./CESTypes";
import { endOverlay, formatError, startOverlay, } from "./CESUtils";
import CESAppInfo from "./CESAppInfo";
import buildForm from './FormFactory';
import RecaptchaFactory from "./components/RecaptchaFactory";
import { StepDescriptor } from "./StepperContext";

/**
 * Automatically creates form, based on retrieved JSON data, inside a given form name element
 * @param {string} formWrapperId - div's id for form render
 * @param {string} formName - form type to be sent (enum)
 * @param {boolean} callPostLogin - if true, it will execute post login validation
 */
export const createProfileForm = (
  formWrapperId: string,
  sendFormFunction: Function
) => new Promise((resolve, reject) => {
  cesRequest.getProfileFields(CESAppInfo.ciamClientId)
    .then((response) => {
      try {
        const { fields, form } = response.data;
        const prefixFields: Record<string, string> = {};
        const countryCode = fields.find((f: any) => f.name === "mobileNumberCountryCode");
        const captcha = fields.find((f: any) => f.name === "captcha");
        if (captcha && captcha.version) {
          CESAppInfo.reCaptchaVer = captcha.version;
        }
        if (captcha && captcha.siteKey) {
          CESAppInfo.reCaptchakey = captcha.siteKey;
        }

        const updatedFields = fields.reduce((acc: Array<any>, field: any) => {
          if (field.name === "mobile" && countryCode) {
            prefixFields[countryCode.name] = field.name;
          }

          if (Object.prototype.hasOwnProperty.call(prefixFields, field.name)) {
            const ownerField = acc.filter((f) => f.name === prefixFields[field.name])[0];
            Object.assign(ownerField, { prefixField: field });
          } else {
            acc.push(field);
          }

          return acc;
        }, []);
        const builtForm = buildForm(formWrapperId, form, updatedFields, sendFormFunction);
        document.getElementById(formWrapperId)!.appendChild(builtForm.getDOM());
        builtForm.setValues();
        resolve(builtForm);
      } catch (error) {
        reject(error);
      }
    })
    .catch((reason) => {
      if (reason.isAuthError) {
        reject(reason.message);
      } else {
        reject(new Error(reason));
      }
    });
})

export const createFormNew = (
  formWrapperId: string,
  formName: string,
  onSubmit: Function,
  overlayConfig?: any,
  formData?: any,
  formValues?: any,
  otp?: string,
  steps?: StepDescriptor[],
) => new Promise<any>((resolve, reject) => {
  startOverlay(overlayConfig)
    .then(() => (formData
      ? Promise.resolve({ data: { ...formData } })
      : cesRequest.getFields(formName, CESAppInfo.ciamClientId, otp)))
    .then((response) => {
      if (response.status === 404) {
        reject(response)
      } else {
        try {
          const { fields } = response.data;
          const prefixFields: Record<string, string> = {};
          const countryCode = fields.find((f: any) => f.name === "mobileNumberCountryCode");
          const captcha = fields.find((f: any) => f.name === "captcha");
          if (captcha && captcha.version) {
            CESAppInfo.reCaptchaVer = captcha.version;
          }
          if (captcha && captcha.siteKey) {
            CESAppInfo.reCaptchakey = captcha.siteKey;
          }
          const updatedFields = fields.reduce((acc: Array<any>, field: any) => {
            if (field.name === "mobile" && countryCode) {
              prefixFields[countryCode.name] = field.name;
            }

            if (formValues) {
              const fieldNames = Object.keys(formValues);
              if (fieldNames.includes(field.name)) {
                field.value = formValues[field.name]
              }
            }

            if (Object.prototype.hasOwnProperty.call(prefixFields, field.name)) {
              const ownerField = acc.filter((f) => f.name === prefixFields[field.name])[0];
              Object.assign(ownerField, { prefixField: field });

              // ECS-1487 Serbian legal regulations accepts gathering age only rather than the date of birth.
              // That's why we hide the birthdate field here and later it's value is going to be populated programatically.
            } else if (!(field.name === "birthdate" && fields.find((item: any) => item.name === "age"))) {
              acc.push(field);
            }

            return acc;
          }, []);
          const form = buildForm(formWrapperId, formName, updatedFields, onSubmit, steps);
          document.getElementById(formWrapperId)!.appendChild(form.getDOM());
          form.setValues();

          resolve(form);
        } catch (error) {
          reject(error);
        }
      }
    }).catch((reason) => {
      if (reason.isAuthError) {
        reject(reason.message);
      } else {
        reject(new Error(reason));
      }
    })
    .finally(() => {
      endOverlay();
    });
})

export const removeAllDomChild = (domId: string) => {
  const domNode = document.getElementById(domId);
  if (!domNode) return;
  while (domNode.firstChild) {
    domNode.removeChild(domNode.firstChild);
  }
}


export const getFormValuesNew = async (form: any, formType?: string, _fields?: any, userValues?: any) => {
  if (!form.isValid()) {
    return undefined;
  }

  const json: any = {
    clientId: CESAppInfo.ciamClientId,
    locale: CESAppInfo.locale,
    user: userValues || {},
    experienceName: CESAppInfo.experienceName,
    deviceId: sessionStorage.getItem("deviceId"),
  };

  if (formType) {
    json.formName = formType;
  }

  if (userValues) {
    return JSON.stringify(json);
  }
  const formValues = await form.getValues();

  Object.keys(formValues).forEach((propertyName) => {
    json.user[propertyName] = formValues[propertyName];
  });

  // Don"t set user with captcha, instead set root captcha
  if (
    Object.prototype.hasOwnProperty.call(json.user, "captcha") ||
    Object.prototype.hasOwnProperty.call(json.user, "recaptcha")
  ) {
    json.captcha = json.user.captcha ?? json.user.recaptcha;
    delete json.user.captcha;
    delete json.user.recaptcha;
  }

  // ECS-1487 Setup birthdate values when age is provided (Serbian legal regulations)
  if (json.user.age
    && formType === FormNames.Registration) {
    const calcBirthdate = dayjs().subtract(json.user.age, "year");
    json.user.birthdate = {
      dateselect_day: calcBirthdate.get("date"),
      dateselect_month: calcBirthdate.get("month") + 1,
      dateselect_year: calcBirthdate.get("year"),
    };
  }

  return json;
}

export const handleSubmissionError = (invalidFields: any) => {
  for (const key in invalidFields) {
    if (!Object.prototype.hasOwnProperty.call(invalidFields, key)) continue;
    const obj = invalidFields[key];

    for (const prop in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, prop)) continue;

      const validationDiv = document.createElement("div");

      validationDiv.setAttribute("class", "validation-error");

      const el = document.getElementById(`div-${key}`);
      if (el) {
        const message = document.createElement("p");
        message.innerHTML = obj[prop];
        message.style.display = "block";

        validationDiv.appendChild(message);

        el.appendChild(validationDiv);
      }
    }
  }
}

const handleErrors = {
  "coppa": {
    canApply: (object: any) => object.name === "birthdate" && object.value === "minYears",
    applyAndReturn: (formWrapperId: string) => {
      handleResponseError(formWrapperId, "coppa.violation");
      return "COPPA error";
    }
  },
  "uniqueAuth": {
    canApply: (object: any) => object.name === FormNames.SignIn && object.value === "rateLimitExceeded",
    applyAndReturn: (formWrapperId: string) => {
      handleResponseError(formWrapperId, "authentication.toomanyattempts");
      return "Authentication error";
    }
  },
  "uniqueEmail": {
    canApply: (object: any) => object.name === "emailAddress" && object.value === "unique",
    applyAndReturn: (formWrapperId: string) => {
      handleResponseError(formWrapperId, "unique.emailAddress");
      return "Unique Email error";
    }
  },
  "passwordValidation": {
    canApply: (object: any) => object.name === "newPassword" && object.value === "passwordValidation",
    applyAndReturn: (formWrapperId: string) => {
      handleResponseError(formWrapperId, "passwordValidation.newPassword");
      return "Password Validation error";
    }
  },
  "emailBlacklist": {
    canApply: (object: any) => object.name === "emailAddress" && object.value === "blacklisted",
    applyAndReturn: (formWrapperId: string) => {
      handleResponseError(formWrapperId, "coppa.violation");
      return "Blacklist error";
    }
  },
  "mobileNumberConflict": {
    canApply: (object: any) => object.name === "mobileNumber" && object.value === "conflict",
    applyAndReturn: (formWrapperId: string) => {
      handleResponseError(formWrapperId, "conflict.mobileNumber");
      return "Conflict Mobile Number error";
    }
  },
};

export const handleResponse = (formWrapper: string, reasonError: any, attributes: any) => {
  const form = document.getElementById(formWrapper);
  const validationDiv = document.createElement("div");

  Object.keys(attributes).forEach((key) => {
    validationDiv.setAttribute(key, attributes[key]);
  });

  formatError(reasonError).forEach(async (errorKey: string) => {
    const message = document.createElement("p");
    await cesRequest.getBundleResource(errorKey)
      .then((errorMessage) => {
        message.innerHTML = errorMessage;
      })
      .catch((error) => {
        message.innerHTML = error;
        console.warn('Missing message in bundle for error: ', errorKey);
      })
    message.style.display = "block";
    validationDiv.appendChild(message);
    if (!form) {
      console.warn('reasonError:', reasonError)
      console.warn(`formWrapper does not exist: ${formWrapper}`)
    } else {
      form.appendChild(validationDiv);
    }
  });
}

export const handleResponseError = (formWrapper: string, reasonError: any) => {
  const attr = { id: "response-error", class: "validation-error" };
  handleResponse(formWrapper, reasonError, attr);
}

/**
 * This method process the error response, if there is a validation error,
 * it will display in the invalid field's error div. If it's a systematic error, it will return an error code.
 * @param formWrapperId Form wrapper id used to find where the error divs
 * and display the error message on it
 * @param reason Object with all the error objects
*/
export const processValidationErrors = (formWrapperId: string, reason: any) => {

  if (reason.status === 502 || reason?.data?.message === "Internal server error") {
    handleResponseError(formWrapperId, "generic.error.internalServerError");
  }

  if (reason.data && reason.data.status && reason.data.errors) {

    let handledError;

    reason.data.errors.forEach((error: any) => {

      if (error.objects && error.objects.length > 0) {
        error.objects.forEach((object: any) => {

          Object.values(handleErrors).forEach((handler: any) => {
            if (handler.canApply(object)) {
              handledError = handler.applyAndReturn(formWrapperId);
            }

          });
        });
      }
    });

    if (handledError) {
      return handledError;
    }

    if (reason.data.status == StatusCodes.FatalError || reason.data.status == String(StatusCodes.FatalError)) {
      return reason.data.errors[0].message;
    }


    const processedError: any = {};
    reason.data.errors.forEach((error: any, idx: number) => {
      if (error.objects && error.objects.length > 0 && idx === 0) {
        error.objects.forEach((reasonObj: any) => {
          processedError[reasonObj.name] = reasonObj.value
        });
      }

      handleResponseError(formWrapperId, error);
    });

    return processedError;
  }

  return "Unkown error";
}

export const getParameterByName = (name: any) => {
  const url = window.location.href;

  name = String(name).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);

  if (!results) return undefined;

  if (!results[2]) return "";

  return decodeURIComponent(results[2].replace(/\+/g, " "));
}

// Only expose setCoppaCookie if used by other modules
export const setCoppaCookie = (): void => {
  const _72hours = new Date();
  _72hours.setDate(_72hours.getDate() + 3);
  const expiresDate = _72hours.toUTCString();
  document.cookie = `coppa=notValid; expires=${expiresDate}; path=/`;
}

const getCoppaCookie = (): string | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split("; " + "coppa" + "=");

  if (parts.length == 2) {
    const result = parts.pop();
    if (result) {
      const finalValue = result.split(";")[0];
      return finalValue;
    }
  }
  return undefined;
}

/**
 * Checks if CoppaCookie exists.
 * @returns {boolean} Returns TRUE if COPPA cookie exists.
 */
export const checkCoppaCookie = () => {
  const coppaCookie = getCoppaCookie();
  if (coppaCookie == null) {
    return false;
  }
  return true;
}

export const setSocialHiddenInfomation = (id: string, value: string) => {
  const inputElement = <HTMLInputElement>document.getElementById(id);
  if (inputElement) {
    inputElement.value = value;
  }
}

export const releaseHandle = () => {
  const responseError = document.getElementById("response-error");

  if (responseError) {
    responseError.remove();
  }
}

export const getRecaptchaToken = async (operation?: string): Promise<string | null> => {
  try {
    const recaptcha = RecaptchaFactory.getInstance(undefined, "captcha");
    if (!recaptcha) return null;
    return await recaptcha.submit(operation);
  } catch (error) {
    console.error("Failed to get reCAPTCHA token:", error);
    return null;
  }
};


export const getSubmitOtpFormValues = async (form: any) => {
  if (!form.isValid()) {
    return undefined
  }

  const values = await form.getValues();
  const captcha = values.captcha ?? values.recaptcha;

  if (values.otp) {
    return {
      otp: values.otp,
      captcha,
      clientId: CESAppInfo.ciamClientId
    }
  }

  return {
    input: `+${values.mobileNumberCountryCode}${values.mobile}`,
    type: "mobileNumber",
    captcha,
    locale: CESAppInfo.locale,
    clientId: CESAppInfo.ciamClientId
  }
}

export const getSubmitEmailValidationFormValues = async (form: any, serviceOtp: any) => {
  if (!form.isValid()) {
    return undefined
  }

  const values = await form.getValues();

  return {
    otp: serviceOtp,
    email: values.emailAddress
  }
}
